import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../components/ClientLayout'; 

const COLORS = {
    SIERRA_GREEN: '#2E5B2E',
    CANTERA_PINK: '#C19A6B',
    CHARCOAL: '#1F2937',
    SOFT_CREAM: '#F9F9F5',
    WHITE: '#FFFFFF',
    DANGER: '#DC2626'
};

// --- COMPONENTES REFINADOS ---
const ClientMenuCard = ({ icon, title, desc, onClick, color }) => (
    <div 
        onClick={onClick} 
        style={styles.menuCard}
        onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.1)';
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
        }}
    >
        <div style={{ ...styles.iconCircle, background: `${color}15`, color: color }}>{icon}</div> 
        <div style={styles.cardTitle}>{title}</div> 
        <p style={styles.cardDesc}>{desc}</p> 
        <div style={{ ...styles.cardBadge, background: color }}>Entrar</div>
    </div>
);

const KPIWidget = ({ title, value, color, icon, onClick, note }) => (
    <div style={{ ...styles.kpiCard, borderLeft: `5px solid ${color}` }} onClick={onClick}>
        <div style={styles.kpiInfo}>
            <span style={styles.kpiTitle}>{title}</span>
            <span style={styles.kpiValue}>{value}</span>
            {note && <span style={styles.kpiNote}>{note}</span>}
        </div>
        <div style={{ ...styles.kpiIcon, color: `${color}40` }}>{icon}</div>
    </div>
);

function Bienvenida() {
    const [dashboardData, setDashboardData] = useState({ 
        ultimoPedido: '...', 
        pedidosPendientes: '0', 
        inventario: '0 Kg', 
        proximoPedido: '...' 
    });
    const navigate = useNavigate();
    
    const storedUser = localStorage.getItem('user');
    const userRole = storedUser ? JSON.parse(storedUser).rol : 'Cliente';
    const isOwner = userRole === 'Cliente' || userRole === 'Admin';

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return; 

        const fetchData = async () => {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            try {
                const [pedidosRes, sucursalesRes, predRes] = await Promise.allSettled([
                    api.get('/pedidos?limit=50', config), 
                    api.get('/sucursales/me', config),
                    api.get('/predicciones/me', config)
                ]);
                
                let updates = {};

                if (pedidosRes.status === 'fulfilled') {
                    const pedidos = pedidosRes.value.data.data || [];
                    updates.pedidosPendientes = pedidos.filter(p => !['ENTREGADO', 'CANCELADO'].includes(p.estado)).length;
                    updates.ultimoPedido = pedidos.length > 0 
                        ? new Date(pedidos[0].createdAt).toLocaleDateString() 
                        : 'Sin pedidos';
                }

                if (sucursalesRes.status === 'fulfilled') {
                    const sucursales = sucursalesRes.value.data || [];
                    let total = 0;
                    sucursales.forEach(s => s.inventarios?.forEach(i => {
                        total += (Number(i.cantidadGrano) || 0) + (Number(i.cantidadMolido) || 0);
                    }));
                    updates.inventario = `${total.toFixed(1)} Kg`;
                }

                if (predRes.status === 'fulfilled') {
                    const preds = predRes.value.data || [];
                    updates.proximoPedido = preds.length > 0 
                        ? new Date(preds[0].fecha).toLocaleDateString() 
                        : 'Calculando...';
                }

                setDashboardData(prev => ({ ...prev, ...updates }));
            } catch (err) { console.error(err); }
        };
        fetchData();
    }, []);

    return (
        <ClientLayout>
            <div style={styles.mainWrapper}>
                
                {/* SECCIÓN IZQUIERDA: ACCIONES */}
                <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Menú de Gestión</h2>
                    <div style={styles.menuGrid}>
                        <ClientMenuCard icon="📝" title="Nuevo Pedido" desc="Solicitar café" onClick={() => navigate('/solicitud-pedido')} color={COLORS.SIERRA_GREEN} />
                        <ClientMenuCard icon="🛒" title="Historial" desc="Tus pedidos" onClick={() => navigate('/pedidos-anteriores')} color={COLORS.SIERRA_GREEN} />
                        
                        {isOwner && (
                            <>
                                <ClientMenuCard icon="🏪" title="Sucursales" desc="Inventarios" onClick={() => navigate('/mis-sucursales')} color={COLORS.CANTERA_PINK} />
                                <ClientMenuCard icon="💳" title="Pagos" desc="Estados de cuenta" onClick={() => navigate('/pagos')} color={COLORS.CANTERA_PINK} />
                                {/* <ClientMenuCard icon="👥" title="Equipo" desc="Gestionar personal" onClick={() => navigate('/mi-equipo')} color={COLORS.SIERRA_GREEN} />  */}
                                <ClientMenuCard icon="⚙️" title="Perfil" desc="Ajustes de cuenta" onClick={() => navigate('/mi-perfil')} color={COLORS.SIERRA_GREEN} />
                            </>
                        )}
                        <ClientMenuCard icon="🔮" title="Predicciones" desc="Consumo futuro" onClick={() => navigate('/predicciones')} color={COLORS.CANTERA_PINK} />
                        <ClientMenuCard icon="📊" title="Análisis" desc="Métricas de venta" onClick={() => navigate('/analisis-negocio')} color={COLORS.SIERRA_GREEN} />
                    </div>
                </section>

                {/* SECCIÓN DERECHA: KPIs */}
                <section style={styles.section}>
                    <h2 style={styles.sectionHeader}>Estado del Negocio</h2>
                    <div style={styles.kpiStack}>
                        <KPIWidget 
                            title="Pedidos Pendientes" 
                            value={dashboardData.pedidosPendientes} 
                            color={dashboardData.pedidosPendientes > 0 ? COLORS.DANGER : COLORS.SIERRA_GREEN} 
                            icon="📦" 
                            onClick={() => navigate('/pedidos-anteriores')} 
                        />
                        <KPIWidget 
                            title="Inventario Global" 
                            value={dashboardData.inventario} 
                            color={COLORS.SIERRA_GREEN} 
                            icon="☕" 
                            onClick={() => navigate(isOwner ? '/mis-sucursales' : '/inventario')} 
                        />
                        <KPIWidget 
                            title="Próxima Compra" 
                            value={dashboardData.proximoPedido} 
                            color={COLORS.CANTERA_PINK} 
                            icon="📅" 
                            note="Fecha estimada de resurtido"
                        />
                        <KPIWidget 
                            title="Último Movimiento" 
                            value={dashboardData.ultimoPedido} 
                            color={COLORS.CHARCOAL} 
                            icon="🕒" 
                        />
                    </div>
                </section>
            </div>
        </ClientLayout>
    );
}

const styles = {
    mainWrapper: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: '40px',
        width: '100%',
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
    },
    sectionHeader: {
        color: COLORS.WHITE,
        fontSize: '1.1rem',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        marginBottom: '20px',
        borderBottom: `2px solid ${COLORS.CANTERA_PINK}`,
        paddingBottom: '8px',
        width: 'fit-content'
    },
    menuGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
        gap: '15px',
    },
    menuCard: {
        background: COLORS.WHITE,
        padding: '20px 15px',
        borderRadius: '16px',
        textAlign: 'center',
        cursor: 'pointer',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        border: '1px solid #eee'
    },
    iconCircle: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '24px',
        marginBottom: '12px'
    },
    cardTitle: {
        fontSize: '0.9rem',
        fontWeight: '700',
        color: COLORS.CHARCOAL,
        marginBottom: '4px'
    },
    cardDesc: {
        fontSize: '0.75rem',
        color: '#666',
        margin: '0 0 12px 0',
        lineHeight: '1.2'
    },
    cardBadge: {
        fontSize: '0.7rem',
        fontWeight: '700',
        color: COLORS.WHITE,
        padding: '4px 12px',
        borderRadius: '20px',
        width: '80%'
    },
    kpiStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    },
    kpiCard: {
        background: COLORS.WHITE,
        padding: '20px',
        borderRadius: '16px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.03)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden'
    },
    kpiInfo: {
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1
    },
    kpiTitle: {
        fontSize: '0.7rem',
        color: '#666',
        fontWeight: '700',
        textTransform: 'uppercase'
    },
    kpiValue: {
        fontSize: '1.5rem',
        fontWeight: '800',
        color: COLORS.CHARCOAL,
        margin: '2px 0'
    },
    kpiNote: {
        fontSize: '0.7rem',
        color: COLORS.SIERRA_GREEN,
        fontStyle: 'italic'
    },
    kpiIcon: {
        fontSize: '40px',
        position: 'absolute',
        right: '-5px',
        bottom: '-10px',
        zIndex: 0
    }
};

export default Bienvenida;